import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Lock, AlertCircle, CheckCircle2 } from 'lucide-react';
import Header from '../components/Header';
import { API } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const { t } = useLang();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tk = params.get('token') || '';
    setToken(tk);
    if (!tk) setError(t('resetLinkMissingToken'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError(t('passwordMin6'));
      return;
    }
    if (password !== confirm) {
      setError(t('passwordsDontMatch'));
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      setError(err?.response?.data?.detail || t('resetPasswordError'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0d0e10] text-white">
      <Header />
      <main className="px-4 py-10">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8 fade-in-up">
            <h1 className="heading-font text-4xl font-black tracking-wide">
              {t('resetPasswordTitle')}
            </h1>
            <p className="mt-2 text-white/70 text-sm">
              {t('resetPasswordSub')}
            </p>
          </div>

          {done ? (
            <div className="p-6 bg-[#1a1b1e] border border-[#2a2b2f] rounded-md text-center fade-in-up" data-testid="reset-done">
              <div className="w-14 h-14 rounded-full bg-[#ff4e15]/20 border-2 border-[#ff4e15] flex items-center justify-center mx-auto">
                <CheckCircle2 size={28} className="text-[#ff4e15]" />
              </div>
              <div className="mt-4 heading-font text-xl font-black">{t('passwordUpdated')}</div>
              <p className="mt-2 text-sm text-white/70">{t('redirectingSignIn')}</p>
              <Link to="/login" className="inline-block mt-4 text-sm text-[#ff4e15] hover:underline">
                {t('signInNow')}
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('newPasswordPlaceholder')}
                  required
                  minLength={6}
                  className="w-full pl-10 pr-4 py-3 bg-[#1a1b1e] border border-[#2a2b2f] focus:border-[#ff4e15] outline-none rounded-sm text-white transition-colors"
                  data-testid="reset-password-input"
                />
              </div>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder={t('confirmPasswordPlaceholder')}
                  required
                  minLength={6}
                  className="w-full pl-10 pr-4 py-3 bg-[#1a1b1e] border border-[#2a2b2f] focus:border-[#ff4e15] outline-none rounded-sm text-white transition-colors"
                  data-testid="reset-confirm-input"
                />
              </div>

              {error && (
                <div className="flex items-start gap-2 text-sm text-[#ff4e15] bg-[#1f1210] border border-[#ff4e15]/40 px-3 py-2 rounded-sm">
                  <AlertCircle size={14} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={busy || !token}
                className="w-full py-3.5 rounded-sm font-bold uppercase tracking-wider text-white bg-[#ff4e15] hover:bg-[#ff6033] disabled:opacity-60 transition-colors"
                data-testid="reset-submit-btn"
              >
                {busy ? t('updating') : t('updatePassword')}
              </button>

              <p className="text-center text-sm text-white/60">
                {t('orBack')}{' '}
                <Link to="/login" className="text-[#ff4e15] hover:underline">{t('backToSignInLink')}</Link>
              </p>
            </form>
          )}
        </div>
      </main>
    </div>
  );
};

export default ResetPassword;
